import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import useAuth from '../context/useAuth';
import qrLogo from '../images/qr-code.jpg'; // Importing the QR code image

const MemberSummaryDashboard = () => {
  const [summaryData, setSummaryData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copySuccess, setCopySuccess] = useState('');
  const { umemid } = useAuth();

  const donationLink = 'https://www.convergepay.com/hosted-payments/?ssl_txn_auth_token=dt99hY6rSmuMKqV%2BFH3NLAAAAXEOL4O6#!/payment-method';

  useEffect(() => {
    const fetchSummaryData = async () => {
      if (!umemid) return; // Avoid unnecessary API calls if umemid is not available
      
      try {
        const path = `/api/members/${umemid}`;
        const response = await api.get(path);
        setSummaryData(response.data.data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSummaryData();
  }, [umemid]);

  const formatDate = (dateStr) => {
    // Check if dateStr is undefined, null, or an empty string
    if (!dateStr) {
        return ''; // Return an empty string or any default value if dateStr is not valid
    }

    // Split the date string into its components
    const [year, month, day] = dateStr.split('-');

    // Return the formatted date as MM/DD/YYYY
    return `${month}/${day}/${year}`;
};

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Donate Now',
          text: 'Join me in supporting this cause! Click the link to donate:',
          url: donationLink,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      navigator.clipboard.writeText(donationLink)
        .then(() => {
          setCopySuccess('Link copied to clipboard!');
        })
        .catch((err) => {
          console.error('Failed to copy: ', err);
        });
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Error fetching member summary data: {error.message || 'Unknown error'}
      </div>
    );
  }

  const { first1, middle1, last1, memberId, totalDonation, lastDonation, lastDonationDate } = summaryData;

  return (
    <div className="container mt-4">
      <h2 className="mb-4">My Donations Summary → Personal Donation Summary</h2>
      {memberId ? (
        <div className="row">
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  Name: {[first1, middle1, last1].filter(Boolean).join(' ') || 'N/A'}
                </h5>
                <ul className="list-group list-group-flush">
                  <li className="list-group-item">
                    <strong>Account Number:</strong> {memberId || 'N/A'}
                  </li>
                  <li className="list-group-item">
                    <strong>Total Lifetime Donations:</strong> {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalDonation || 0)}
                  </li>
                  <li className="list-group-item">
                    <strong>Recent Donation:</strong> {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(lastDonation || 0)}
                  </li>
                  <li className="list-group-item">
                    <strong>Recent Donation Date:</strong> {lastDonationDate ? formatDate(lastDonationDate) : 'N/A'}
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="col-md-6 d-flex flex-column align-items-center justify-content-center">
            <a href={donationLink} className="btn btn-primary mb-3" target="_blank" rel="noopener noreferrer">
              Donate Now using Credit Card
            </a>
            <img 
              src={qrLogo} 
              alt="QR Code" 
              style={{ width: '150px', height: '150px' }} 
            />
            <p className="mt-2">Scan the QR code to donate</p>
            
            {/* Share button */}
            <button onClick={handleShare} className="btn btn-secondary mt-3">
              Share Donation Link
            </button>
            {copySuccess && <p className="text-success mt-2">{copySuccess}</p>}
          </div>
        </div>
      ) : (
        <div className="text-center mt-4">
          <p>No member summary data available.</p>
        </div>
      )}
    </div>
  );
};

export default MemberSummaryDashboard;